import { Button, Form, type FormProps, Input, Modal, Typography, Select, Flex } from 'antd'
import React, { useState } from 'react'

import { type InviteUserBody, SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { School, SchoolSubscription } from '../../../../../types'

type ModalProps = {
    school: School
    isOpen: boolean
    onClose: () => void
    refetch: () => Promise<void>
    availableSchoolSubscriptions: SchoolSubscription[]
    existingEmails: string[]
}

type FieldType = {
    role?: string
    email?: string
    schoolSubscriptionIds?: string[]
}

export const InviteSchoolUserModal: React.FC<ModalProps> = ({
    isOpen,
    school,
    onClose,
    refetch,
    availableSchoolSubscriptions: rawAvailableSchoolSubscriptions,
    existingEmails
}) => {
    const { showNotification } = useNotifications()
    const [isLoading, setIsLoading] = useState(false)
    const confirm = useConfirmation()

    const onFinish: FormProps<FieldType>['onFinish'] = async values => {
        const schoolSubscriptionIds = values.schoolSubscriptionIds || []
        const body: InviteUserBody = {
            email: values.email!,
            role: values.role ?? 'USER',
            schoolSubscriptionIds
        }

        let confirmed = true
        try {
            if (schoolSubscriptionIds.length === 0) {
                confirmed = await confirm({
                    title: 'No subscriptions assigned to this invite?',
                    description: 'Do you want to proceed?'
                })
            }

            if (confirmed) {
                setIsLoading(true)
                await SchoolService.invite(school.id!, body)
                showNotification({ title: 'Invite successully added' })

                await refetch()
                onClose()
            }
        } catch (err: any) {
            const message = err.response.data ? err.response.data : err.toString()

            showNotification({ title: 'Could not add the invite', type: 'error', description: message })
        } finally {
            setIsLoading(false)
        }
    }

    if (!school) {
        return null
    }

    const availableSchoolSubscriptions = rawAvailableSchoolSubscriptions.filter(
        ss => ss.subscription.status !== 'CANCELLED' && ss.subscription.status !== 'COMPLETED'
    )

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            <Flex vertical gap="middle">
                <Typography.Title level={4}>Invite User to {school.name}</Typography.Title>
                <Typography.Text>
                    If the user already exists, they will be added to the school, if not they will receive an invite.
                </Typography.Text>
                <Form
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 14 }}
                    layout="horizontal"
                    disabled={false}
                    onFinish={onFinish}
                >
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { required: true, message: 'Please enter an email' },
                            {
                                type: 'email', // Built-in email format check
                                message: 'Please enter a valid email address!'
                            },
                            () => ({
                                validator(_, value) {
                                    if (
                                        existingEmails.find(
                                            (email: string) => email.toLowerCase() === value.toLowerCase()
                                        )
                                    ) {
                                        return Promise.reject(new Error('User already exists in this school.'))
                                    }
                                    return Promise.resolve()
                                }
                            })
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="Role" name="role">
                        <Select
                            defaultValue="USER"
                            allowClear={false}
                            options={[
                                { value: 'USER', label: 'User' },
                                { value: 'ADMIN', label: 'Admin' },
                                { value: 'PARENT', label: 'Parent' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item label="Subscriptions" name="schoolSubscriptionIds">
                        <Select
                            style={{ minWidth: '300px' }}
                            mode="multiple"
                            options={availableSchoolSubscriptions.map(s => ({
                                label:
                                    s.subscription.name ||
                                    s.subscription.subscriptionProducts.map(sp => sp.product.key).join(', '),
                                value: s.id,
                                color: 'green'
                            }))}
                        />
                    </Form.Item>
                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            Invite
                        </Button>
                    </Form.Item>
                </Form>
            </Flex>
        </Modal>
    )
}
